import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Search, Download, ChevronDown, ChevronRight, ChevronLeft, ChevronRight as ChevronRightPag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function UpstreamError({ detail }) {
  return (
    <div data-testid="upstream-error" style={{
      background: '#fef9ec', border: '1px solid #fde68a', borderRadius: 8,
      padding: '20px 24px', margin: '24px 0',
    }}>
      <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6, fontSize: 14 }}>
        Upstream API Unavailable
      </div>
      <div style={{ color: '#78350f', fontSize: 13 }}>{detail}</div>
      <div style={{ color: '#92400e', fontSize: 12, marginTop: 8 }}>
        This page requires a lupnxtlaravel account. Contact your administrator.
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  const active = String(value).toLowerCase() === 'active' || value === 1 || value === true;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: active ? '#dcfce7' : '#f1f5f9',
      color: active ? '#15803d' : '#64748b',
    }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function DetailPanel({ row }) {
  return (
    <div
      data-testid="journey-detail-expand"
      style={{
        padding: '14px 24px 18px',
        background: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
        {row.description && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Description</div>
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{row.description}</div>
          </div>
        )}
        {row.key_outcomes && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Key Outcomes</div>
            <div style={{ fontSize: 13, color: '#475569' }}>{row.key_outcomes}</div>
          </div>
        )}
        {row.feature_products && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Feature Products</div>
            <div style={{ fontSize: 13, color: '#475569' }}>{row.feature_products}</div>
          </div>
        )}
        {row.duration && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Duration</div>
            <div style={{ fontSize: 13, color: '#475569' }}>{row.duration}</div>
          </div>
        )}
        {row.target_role && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Target Role</div>
            <div style={{ fontSize: 13, color: '#475569' }}>{row.target_role}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const flatRows = rows.map(r => ({
    id: r.id ?? '',
    name: r.name ?? '',
    target_role: r.target_role ?? '',
    solution_area: r.solution_area ?? '',
    duration: r.duration ?? '',
    courses_count: r.courses_count ?? '',
    status: r.status ?? '',
    description: r.description ?? '',
  }));
  const keys = Object.keys(flatRows[0]);
  const lines = [
    keys.join(','),
    ...flatRows.map(r => keys.map(k => {
      const v = r[k] ?? '';
      return String(v).includes(',') ? `"${String(v).replace(/"/g, '""')}"` : v;
    }).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function LearningJourneysAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upstreamError, setUpstreamError] = useState(null);
  const [search, setSearch] = useState('');
  const [dateType, setDateType] = useState('enrollment'); // enrollment | completion
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const PER_PAGE = 50;

  const fetchData = useCallback(async (pg = 1, q = '', dt = null) => {
    setLoading(true);
    setError(null);
    setUpstreamError(null);
    try {
      const params = { page: pg, per_page: PER_PAGE, date_type: dt ?? dateType };
      if (q) params.search = q;
      const res = await axios.get(`${API}/api/lup/v2/admin/reports/journey-report`, { params, withCredentials: true });
      const data = res.data;
      if (Array.isArray(data)) {
        setRows(data);
        setMeta(null);
      } else if (data?.data) {
        setRows(data.data);
        setMeta(data.meta || null);
      } else {
        setRows([]);
      }
    } catch (e) {
      if (e.response?.status === 503 || e.response?.status === 502) {
        setUpstreamError('The journey report endpoint is not yet available on the upstream service. It will be available once deployed.');
      } else if (e.response?.status === 404) {
        setUpstreamError('The journey report endpoint has not yet been deployed on the upstream service.');
      } else {
        setError(e.response?.data?.detail || 'Failed to load journey data.');
      }
    } finally {
      setLoading(false);
    }
  }, [dateType]);

  useEffect(() => { fetchData(1, search); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData(1, search);
  };

  const handlePage = (newPage) => {
    setPage(newPage);
    fetchData(newPage, search);
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => (r.name || '').toLowerCase().includes(q));
  }, [rows, search]);

  const lastPage = meta?.last_page || (meta?.total ? Math.ceil(meta.total / PER_PAGE) : null);

  const COLUMNS = useMemo(() => {
    if (!rows.length) return [];
    const preferred = [
      { key: 'id',           label: 'ID' },
      { key: 'name',         label: 'Journey Name', maxWidth: 300 },
      { key: 'target_role',  label: 'Target Role', maxWidth: 180 },
      { key: 'solution_area', label: 'Solution Area', maxWidth: 160 },
      { key: 'courses_count', label: 'Courses', align: 'right' },
      { key: 'status',        label: 'Status', render: v => <StatusPill value={v} /> },
    ];
    const available = new Set(Object.keys(rows[0]));
    return preferred.filter(c => available.has(c.key));
  }, [rows]);

  return (
    <div data-testid="journeys-admin" style={{ padding: '0 16px' }}>
      <div style={{ padding: '20px 0 12px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Learning Journeys Admin</h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
          View and manage learning journeys via lupnxtlaravel. Click a row to expand details.
        </p>
      </div>

      {upstreamError && <UpstreamError detail={upstreamError} />}

      {!upstreamError && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  data-testid="journey-admin-search"
                  type="text"
                  placeholder="Search journey name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ height: 34, paddingLeft: 32, paddingRight: 12, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 7, outline: 'none', width: 260 }}
                />
              </div>
              <button type="submit" style={{ height: 34, padding: '0 16px', fontSize: 13, fontWeight: 600, background: '#194449', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                Search
              </button>

              {/* Date Type toggle */}
              <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 7, overflow: 'hidden' }}>
                {[
                  { value: 'enrollment',  label: 'Enrollment Date' },
                  { value: 'completion',  label: 'Completion Date' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`journey-date-type-${opt.value}`}
                    onClick={() => {
                      setDateType(opt.value);
                      setPage(1);
                      fetchData(1, search, opt.value);
                    }}
                    style={{
                      height: 34, padding: '0 13px', border: 'none',
                      background: dateType === opt.value ? '#194449' : '#fff',
                      color: dateType === opt.value ? '#fff' : '#64748b',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </form>
            <button
              data-testid="journey-admin-export-btn"
              onClick={() => exportCSV(rows, 'learning_journeys.csv')}
              disabled={!rows.length}
              style={{ height: 34, padding: '0 14px', fontSize: 13, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table" data-testid="journeys-admin-table">
                <thead>
                  <tr>
                    <th style={{ width: 24 }} />
                    {COLUMNS.map(c => (
                      <th key={c.key} style={{ textAlign: c.align === 'right' ? 'right' : 'left' }}>{c.label}</th>
                    ))}
                    {!COLUMNS.length && <th>Loading columns…</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={(COLUMNS.length || 0) + 1} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>Loading…</td></tr>
                  ) : error ? (
                    <tr><td colSpan={(COLUMNS.length || 0) + 1} style={{ textAlign: 'center', color: '#dc2626', padding: '24px 0', fontSize: 13 }}>{error}</td></tr>
                  ) : filteredRows.length === 0 ? (
                    <tr><td colSpan={(COLUMNS.length || 0) + 1} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>No journeys found.</td></tr>
                  ) : (
                    filteredRows.map((row, i) => (
                      <React.Fragment key={row.id ?? i}>
                        <tr
                          data-testid={`journey-admin-row-${row.id ?? i}`}
                          onClick={() => setExpandedId(expandedId === (row.id ?? i) ? null : (row.id ?? i))}
                          style={{ cursor: 'pointer', background: expandedId === (row.id ?? i) ? '#f8fafc' : '' }}
                        >
                          <td style={{ textAlign: 'center', color: '#94a3b8' }}>
                            {expandedId === (row.id ?? i) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </td>
                          {COLUMNS.map(c => (
                            <td key={c.key} style={{ textAlign: c.align === 'right' ? 'right' : 'left', fontSize: 13, maxWidth: c.maxWidth || 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: c.key === 'name' ? 600 : 400, color: c.key === 'name' ? '#1e293b' : '#475569' }}>
                              {c.render ? c.render(row[c.key]) : (row[c.key] ?? '—')}
                            </td>
                          ))}
                        </tr>
                        {expandedId === (row.id ?? i) && (
                          <tr data-testid={`journey-admin-detail-${row.id ?? i}`}>
                            <td colSpan={(COLUMNS.length || 0) + 1} style={{ padding: 0, borderBottom: '2px solid #e2e8f0' }}>
                              <DetailPanel row={row} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {lastPage && lastPage > 1 && (
            <div data-testid="journeys-admin-pagination" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>
                Page {page} of {lastPage}{meta?.total ? ` (${meta.total.toLocaleString()} total)` : ''}
              </span>
              <button onClick={() => handlePage(page - 1)} disabled={page <= 1} style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page > 1 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => handlePage(page + 1)} disabled={page >= lastPage} style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page < lastPage ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronRightPag size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
